import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { FiUpload, FiFileText, FiTrash } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";

import useFileValidation from "../hooks/useFileValidation";
import { notifyUser } from "../utils/notifyUser";

const FileCard = ({ path, displayName, signatureName, signedAt }) => {
    FileCard.propTypes = {
        path: PropTypes.string.isRequired,
        displayName: PropTypes.string,
        signatureName: PropTypes.string,
        signedAt: PropTypes.string,
    };
    const fileName = path.split("/").pop();
    const ext = fileName.split(".").pop().toLowerCase();

    const renderPreview = () => {
        if(["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
            return (
                <img
                    src={path}
                    alt={fileName}
                    className="h-24 sm:h-28 lg:h-32 w-full object-cover rounded-md"
                />
            );
        } else if(["mp4", "webm", "ogg"].includes(ext)) {
            return (
                <video
                    controls
                    src={path}
                    className="h-24 sm:h-28 lg:h-32 w-full rounded-md"
                />
            );
        } else if(["mp3", "wav", "ogg"].includes(ext)) {
            return <audio controls src={path} className="w-full mt-1" />;
        } else if(["pdf"].includes(ext)) {
            return (
                <div className="w-full h-24 sm:h-28 lg:h-32 overflow-hidden rounded-md">
                    <iframe
                        src={path}
                        title={fileName}
                        className="transform origin-top-left w-full h-full"
                    />
                </div>
            );
        } else {
            return (
                <div className="flex flex-col items-center justify-center h-24 sm:h-28 lg:h-32 text-gray-500">
                    <FiFileText className="text-xl sm:text-2xl lg:text-3xl" />
                    <span className="text-xs mt-1">{ext.toUpperCase()}</span>
                </div>
            );
        }
    };

    return (
        <div className="bg-white shadow-sm rounded-xl border p-2 sm:p-3 lg:p-4 w-full max-w-xs mx-auto">
            <div className="mb-2">{renderPreview()}</div>
            <p className="text-xs sm:text-sm truncate text-navy font-medium leading-tight">
                {displayName || fileName}
            </p>
            {signedAt && (
                <p className="mt-1 text-[11px] font-medium text-green-700">
                    Electronically signed
                    {signatureName ? ` by ${signatureName}` : ""}{" "}
                    on {new Date(signedAt).toLocaleString()}
                </p>
            )}
            <a
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 underline block mt-1 hover:text-blue-800"
            >
                Open
            </a>
        </div>
    );
};

const getDocumentUrl = (doc) => {
    if(
        /^https?:\/\//i.test(doc.source_url || "") ||
        doc.source_url?.startsWith("/")
    ) {
        return doc.source_url;
    }

    return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.filename}`;
};

const UserUploadsPage = ({ userId, user }) => {
    const [uploads, setUploads] = useState({
        logo: null,
        documents: [],
        media: [],
    });
    const [additionalUploads, setAdditionalUploads] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [, setApproved] = useState(false);
    const [hasPending, setHasPending] = useState(false);
    const [adminUploads, setAdminUploads] = useState([]);
    const electronicallySignedDocuments = adminUploads.filter(
        (doc) => doc.electronically_signed_at,
    );
    const otherAdminUploads = adminUploads.filter(
        (doc) => !doc.electronically_signed_at,
    );
    // Fetch admin-assigned documents for this user
    useEffect(() => {
        const fetchAdminUploads = async () => {
            try {
                const actualUserId = userId || user?.id;
                if(!actualUserId) return;
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/user/${actualUserId}`,
                    { withCredentials: true },
                );
                setAdminUploads(res.data || []);
            } catch{
                // No toast for normal users if not found
            }
        };
        fetchAdminUploads();
    }, [userId, user]);

    // File validation hook
    const {
        validateFile,
        getAcceptString,
        loading: fileValidationLoading,
    } = useFileValidation();

    useEffect(() => {
        const fetchUploads = async () => {
            try {
                const email = user?.email;
                if(!email) {
                    console.error("No user email available");
                    return;
                }

                let res;
                if(user.role === "investor") {
                    // For investors, use investor-info endpoint
                    const res = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-info`,
                        {
                            params: { email },
                            withCredentials: true,
                        },
                    );
                    const {
                        additionalUploads: approvedAdditionalUploads,
                        userId: userIdFromResponse,
                    } = res.data;

                    // Use the userId from the response if not provided as prop
                    const actualUserId = userId || userIdFromResponse;

                    // Process additional uploads from the approved column
                    const processedAdditionalUploads = Array.isArray(
                        approvedAdditionalUploads,
                    )
                        ? approvedAdditionalUploads.map((file) => ({
                              name: file.split("/").pop(),
                              path: file.startsWith("/uploads/")
                                  ? file.substring(1)
                                  : file.startsWith("uploads/")
                                    ? file
                                    : file,
                          }))
                        : [];

                    setUploads({
                        logo: null,
                        investmentLogo: null,
                        documents: [],
                        media: [],
                    });

                    // Set the approved additional uploads
                    setAdditionalUploads(processedAdditionalUploads);

                    // Fetch pending status separately
                    const { data } = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${actualUserId}/additional-uploads?userRole=${user.role}`,
                        {
                            withCredentials: true,
                        },
                    );
                    setApproved(data.approved);
                    setHasPending(data.hasPending || false);
                } else {
                    // For entrepreneurs, use entrepreneur-info endpoint
                    res = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-info`,
                        {
                            params: { email },
                            withCredentials: true,
                        },
                    );
                    const {
                        companyLogo,
                        investmentLogo,
                        uploadedFiles,
                        campaignMediaUploads,
                        additionalUploads: approvedAdditionalUploads,
                        userId: userIdFromResponse,
                    } = res.data;

                    // Use the userId from the response if not provided as prop
                    const actualUserId = userId || userIdFromResponse;

                    // Process uploadedFiles to create documents array with proper structure
                    let documents = [];
                    if(
                        typeof uploadedFiles === "object" &&
                        uploadedFiles !== null
                    ) {
                        if(Array.isArray(uploadedFiles)) {
                            // If it's an array, assume these are just filenames (should not happen with new backend, but fallback)
                            documents = uploadedFiles.map((file) => ({
                                name: file.split("/").pop(),
                                path: file,
                            }));
                        } else {
                            // If it's an object with document types, use backend-provided paths as-is
                            documents = [];
                            Object.entries(uploadedFiles).forEach(
                                ([docType, files]) => {
                                    const fileArray = Array.isArray(files)
                                        ? files
                                        : [files];
                                    fileArray.forEach((file) => {
                                        if(file && typeof file === "string") {
                                            documents.push({
                                                name: file.split("/").pop(),
                                                path: file,
                                                type: docType,
                                            });
                                        }
                                    });
                                },
                            );
                        }
                    }

                    // Process additional uploads from the approved column
                    const processedAdditionalUploads = Array.isArray(
                        approvedAdditionalUploads,
                    )
                        ? approvedAdditionalUploads.map((file) => ({
                              name: file.split("/").pop(),
                              path: file.startsWith("/uploads/")
                                  ? file.substring(1)
                                  : file.startsWith("uploads/")
                                    ? file
                                    : file,
                          }))
                        : [];

                    setUploads({
                        logo: companyLogo,
                        investmentLogo: investmentLogo,
                        documents: documents,
                        media: (() => {
                            try {
                                // Handle both string (JSON) and array formats for campaignMediaUploads
                                let mediaArray = [];
                                if(typeof campaignMediaUploads === "string") {
                                    mediaArray =
                                        JSON.parse(campaignMediaUploads);
                                } else if(
                                    Array.isArray(campaignMediaUploads)
                                ) {
                                    mediaArray = campaignMediaUploads;
                                }

                                return Array.isArray(mediaArray)
                                    ? mediaArray.map((file) => {
                                          if(typeof file === "string") {
                                              // If already a full URL (starts with http), use as-is
                                              if(
                                                  file.startsWith("http://") ||
                                                  file.startsWith("https://")
                                              ) {
                                                  return {
                                                      name: file
                                                          .split("/")
                                                          .pop(),
                                                      path: file,
                                                  };
                                              }
                                              // If backend-provided path (starts with /uploads/ or uploads/), just prefix backend URL if needed
                                              const backendUrl =
                                                  process.env
                                                      .REACT_APP_BACKEND_URL;
                                              if(
                                                  file.startsWith("/uploads/")
                                              ) {
                                                  return {
                                                      name: file
                                                          .split("/")
                                                          .pop(),
                                                      path: `${backendUrl}${file}`,
                                                  };
                                              } else if(
                                                  file.startsWith("uploads/")
                                              ) {
                                                  return {
                                                      name: file
                                                          .split("/")
                                                          .pop(),
                                                      path: `${backendUrl}/${file}`,
                                                  };
                                              } else {
                                                  // Fallback: treat as filename only
                                                  return {
                                                      name: file,
                                                      path: `${backendUrl}/uploads/${actualUserId}/media/${file}`,
                                                  };
                                              }
                                          }
                                          return file;
                                      })
                                    : [];
                            } catch(error) {
                                console.error(
                                    "Error processing campaignMediaUploads:",
                                    error,
                                );
                                return [];
                            }
                        })(),
                    });

                    // Set the approved additional uploads
                    setAdditionalUploads(processedAdditionalUploads);

                    // Fetch pending status separately
                    const { data } = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${actualUserId}/additional-uploads?userRole=${user.role}`,
                        {
                            withCredentials: true,
                        },
                    );
                    setApproved(data.approved);
                    setHasPending(data.hasPending || false);
                }
            } catch(err) {
                console.error("Error fetching uploads:", err);
                // Set default empty arrays on error
                setUploads({
                    logo: null,
                    investmentLogo: null,
                    documents: [],
                    media: [],
                });
            }
        };
        if(user?.email && user?.role) fetchUploads();
    }, [userId, user]);

    const removeFile = (index) => {
        setNewFiles(newFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        // Check if any files are selected
        if(!newFiles || newFiles.length === 0) {
            notifyUser(null, "error", "Please upload at least one file before submitting.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Validate each file for allowed type and size
        let allValid = true;
        let errorMessages = [];
        for(let file of newFiles) {
            // Determine file type: document or image
            const ext = file.name.split(".").pop().toLowerCase();
            let fileType = null;
            if(["doc", "docx", "xlsx", "pdf", "pptx", "xls"].includes(ext)) {
                fileType = "documents";
            } else if(["gif", "png", "jpeg", "jpg"].includes(ext)) {
                fileType = "images";
            } else {
                fileType = null;
            }
            if(!fileType) {
                allValid = false;
                errorMessages.push(
                    `File '${file.name}' is not an allowed type.`,
                );
                continue;
            }
            const { isValid, errors } = validateFile(file, fileType);
            if(!isValid) {
                allValid = false;
                errorMessages.push(
                    ...errors.map((e) => `File '${file.name}': ${e}`),
                );
            }
        }
        if(!allValid) {
            errorMessages.forEach((msg) => notifyUser(null, "error", msg, "toast", null, null, null, null, null, false, false, "top-center"));
            return;
        }

        // Get the actual user ID from user data if not provided as prop
        const actualUserId = userId || user?.id;
        if(!actualUserId) {
            notifyUser(null, "error", "User ID not available", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const formData = new FormData();
        for(let file of newFiles) formData.append("additionalFiles", file);
        formData.append("docType", "additional-documents");

        // Final validation - ensure FormData contains files
        if(
            !formData.getAll("additionalFiles") ||
            formData.getAll("additionalFiles").length === 0
        ) {
            notifyUser(null, "error", "No files found to upload. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        console.log("User upload attempt:", {
            fileCount: newFiles.length,
            fileNames: newFiles.map((f) => f.name),
            formDataFiles: formData.getAll("additionalFiles").length,
        });

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload?userId=${actualUserId}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            const uploaded = res.data.files
                .filter((f) => f.type === "additional")
                .map((f) => f.path);
            // Only send the newly uploaded files
            const newFileNames = uploaded.map((p) => p.split("/").pop());

            console.log("User upload successful:", {
                uploadedFiles: newFileNames,
                totalCount: newFileNames.length,
            });

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/files/additional`,
                {
                    userId: actualUserId,
                    additionalFiles: newFileNames,
                    userRole: user?.role,
                },
            );

            notifyUser(null, "success", "Files uploaded successfully and sent for admin approval. They will appear in your document center once approved.", "toast", null, null, null, null, null, false, false, "top-center");

            // Refetch uploads to update the pending notification
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${actualUserId}/additional-uploads?userRole=${user?.role}`,
                    { withCredentials: true },
                );
                setApproved(data.approved);
                setHasPending(data.hasPending || false);

                // Refetch the main uploads data to get updated additional uploads
                const email = user?.email;
                if(email) {
                    const res = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-info`,
                        {
                            params: { email },
                            withCredentials: true,
                        },
                    );
                    const { additionalUploads: updatedAdditionalUploads } =
                        res.data;
                    const processedAdditionalUploads = Array.isArray(
                        updatedAdditionalUploads,
                    )
                        ? updatedAdditionalUploads.map((file) => ({
                              name: file.split("/").pop(),
                              path: file.startsWith("/uploads/")
                                  ? file.substring(1)
                                  : file.startsWith("uploads/")
                                    ? file
                                    : file,
                          }))
                        : [];
                    setAdditionalUploads(processedAdditionalUploads);
                }
            } catch(refetchErr) {
                console.error("Error refetching uploads:", refetchErr);
                // Don't show error toast for refetch failures - upload was successful
            }

            setNewFiles([]);
        } catch(err) {
            console.error("User upload failed:", err);
            if(
                err.response &&
                err.response.data &&
                err.response.data.message
            ) {
                notifyUser(null, "error", `Upload failed: ${err.response.data.message}`, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(err.response && err.response.status === 400) {
                notifyUser(null, "error", "Invalid file upload. Please check your files and try again.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Upload failed. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const renderSection = (title, files, folder) => {
        // Ensure files is always an array
        const fileArray = Array.isArray(files) ? files : [];
        const actualUserId = userId || user?.id;

        return (
            <div className="font-poppins">
                <div className="mb-8 sm:mb-10 lg:mb-12">
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                        <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy uppercase">
                            {title}
                        </h2>
                    </div>
                    {fileArray.length ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4 lg:gap-5">
                            {fileArray.map((fileObj, i) => {
                                // Handle both old string format and new object format
                                // const fileName =
                                //     typeof fileObj === "string"
                                //         ? fileObj
                                //         : fileObj.name;
                                let filePath;

                                if(typeof fileObj === "string") {
                                    // Legacy format - construct path
                                    filePath = `${process.env.REACT_APP_BACKEND_URL}/uploads/${actualUserId}/${folder}/${fileObj}`;
                                } else {
                                    // New object format - use path as-is but ensure proper URL construction
                                    const path = fileObj.path;
                                    if(
                                        path.startsWith("http://") ||
                                        path.startsWith("https://")
                                    ) {
                                        // Already a full URL
                                        filePath = path;
                                    } else if(path.startsWith("uploads/")) {
                                        // Full path already provided - just add backend URL
                                        filePath = `${process.env.REACT_APP_BACKEND_URL}/${path}`;
                                    } else {
                                        // Partial path - needs folder prefix
                                        filePath = `${process.env.REACT_APP_BACKEND_URL}/uploads/${actualUserId}/${folder}/${path}`;
                                    }
                                }

                                return <FileCard key={i} path={filePath} />;
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-8 sm:py-12">
                            <p className="text-sm sm:text-base text-gray-500">
                                No files uploaded.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="font-poppins">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
                <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy uppercase mb-6">
                        Documents center
                    </h1>

                    {!hasPending ? (
                        <div className="flex flex-col items-start gap-3 text-left bg-gray-50 p-4 rounded-lg shadow-sm border w-full">
                            <p className="text-sm text-gray-700 font-medium">
                                Add Additional Documents
                            </p>
                            <p className="text-xs text-gray-500">
                                (Pitch decks, reports, visuals — Max size: 10MB)
                            </p>

                            <label className="cursor-pointer bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 hover:border-navy hover:text-navy transition duration-150 w-full lg:w-auto text-center">
                                Choose Files
                                <input
                                    type="file"
                                    multiple
                                    accept={
                                        getAcceptString("documents") +
                                        "," +
                                        getAcceptString("images")
                                    }
                                    onChange={(e) =>
                                        setNewFiles([...e.target.files])
                                    }
                                    className="hidden"
                                    disabled={fileValidationLoading}
                                />
                            </label>

                            {newFiles.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-700">
                                        Selected Files:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {newFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between bg-white p-2 rounded border min-w-0 max-w-xs"
                                            >
                                                <span className="text-sm truncate flex-1">
                                                    {file.name}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        removeFile(index)
                                                    }
                                                    className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                                                >
                                                    <FiTrash className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={handleUpload}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-navy text-white rounded-md hover:bg-[#003656] text-sm w-full lg:w-auto"
                                disabled={fileValidationLoading}
                            >
                                <FiUpload className="text-base" />
                                Upload Files
                            </button>
                        </div>
                    ) : (
                        <div className="mt-5 mb-6 p-3 rounded-lg bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 shadow flex justify-center items-center">
                            <div className="flex flex-row items-center gap-2 justify-center w-full">
                                <FaExclamationTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600" />
                                <span className="block mb-0 text-center text-sm">
                                    Your additional uploads are awaiting admin
                                    approval.
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Company Logo */}
                {uploads.logo && (
                    <div className="mb-8 sm:mb-10">
                        <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy mb-3 sm:mb-4 uppercase">
                            Company Logo
                        </h2>
                        <img
                            src={
                                uploads.logo.startsWith("uploads/")
                                    ? `${process.env.REACT_APP_BACKEND_URL}/${uploads.logo}`
                                    : `${
                                          process.env.REACT_APP_BACKEND_URL
                                      }/uploads/${userId || user?.id}/logo/${
                                          uploads.logo
                                      }`
                            }
                            alt="Company Logo"
                            className="h-24 w-24 sm:h-32 sm:w-32 lg:h-40 lg:w-40 object-cover border rounded-lg"
                        />
                    </div>
                )}

                {/* Investment Logo */}
                {uploads.investmentLogo && (
                    <div className="mb-8 sm:mb-10">
                        <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy mb-3 sm:mb-4 uppercase">
                            Investment Logo
                        </h2>
                        <img
                            src={
                                uploads.investmentLogo.startsWith("/uploads/")
                                    ? `${process.env.REACT_APP_BACKEND_URL}${uploads.investmentLogo}`
                                    : uploads.investmentLogo.startsWith(
                                            "uploads/",
                                        )
                                      ? `${process.env.REACT_APP_BACKEND_URL}/${uploads.investmentLogo}`
                                      : `${
                                            process.env.REACT_APP_BACKEND_URL
                                        }/uploads/${
                                            userId || user?.id
                                        }/investmentLogo/${
                                            uploads.investmentLogo
                                        }`
                            }
                            alt="Investment Logo"
                            className="h-24 w-24 sm:h-32 sm:w-32 lg:h-40 lg:w-40 object-cover border rounded-lg"
                        />
                    </div>
                )}

                {/* Electronically Signed Documents */}
                {electronicallySignedDocuments.length > 0 && (
                    <div className="mb-8 sm:mb-10 lg:mb-12">
                        <div className="flex justify-between items-center mb-4 sm:mb-6">
                            <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy uppercase">
                                Electronically Signed Documents
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4 lg:gap-5">
                            {electronicallySignedDocuments.map((doc, i) => (
                                <FileCard
                                    key={`${doc.source}-${i}`}
                                    path={getDocumentUrl(doc)}
                                    displayName={doc.originalname}
                                    signatureName={doc.signature_text}
                                    signedAt={doc.electronically_signed_at}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Admin Uploads */}
                {otherAdminUploads.length > 0 && (
                    <div className="mb-8 sm:mb-10 lg:mb-12">
                        <div className="flex justify-between items-center mb-4 sm:mb-6">
                            <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy uppercase">
                                Admin/Additional Uploads
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4 lg:gap-5">
                            {otherAdminUploads.map((doc, i) => (
                                <FileCard
                                    key={i}
                                    path={getDocumentUrl(doc)}
                                    displayName={doc.originalname}
                                    signatureName={doc.signature_text}
                                    signedAt={doc.electronically_signed_at}
                                    type={doc.mimetype || "document"}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Documents */}
                {renderSection("Documents", uploads.documents, "documents")}

                {/* Media */}
                {renderSection("Media", uploads.media, "media")}

                {/* Additional Uploads */}
                {Array.isArray(additionalUploads) &&
                    additionalUploads.length > 0 &&
                    renderSection(
                        "Additional Uploads",
                        additionalUploads,
                        "additional-documents",
                    )}
            </div>
        </div>
    );
};

UserUploadsPage.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
    }),
};

export default UserUploadsPage;
